import { useState } from "react";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";

const STATUS_BADGE = {
  Active: "bg-emerald-50 text-emerald-700",
  Inactive: "bg-gray-100 text-gray-600",
  Pending: "bg-amber-50 text-amber-700",
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-500">—</span>;
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
};

const clientName = (c) =>
  c.company_name ||
  `${c.primary_user?.first_name || ""} ${c.primary_user?.last_name || ""}`.trim() ||
  "—";

const ClientsCard = ({
  pool,
  allClients,
  isEditing,
  setIsEditing,
  formState,
  handleMultiSelect,
  setPool,
}) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/client-pools/${pool.id}`, {
        client_ids: formState.client_ids,
      });

      if (res.data.success) {
        const updated = await api.get(`/client-pools/${pool.id}`);
        if (updated.data.success) {
          setPool(updated.data.data);
        }
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Failed to update clients:", err);
      toast.error("Failed to update clients. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const HEADERS_VIEW = ["Name", "Type", "Status", "Country"];
  const HEADERS_EDIT = ["Select", "Name", "Type", "Status", "Country"];
  const headers = isEditing ? HEADERS_EDIT : HEADERS_VIEW;

  return (
    <section>
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Clients
          </h3>
          <span className="text-[10px] font-semibold text-gray-500">
            ({pool.clients?.length || 0})
          </span>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className={btn("primary", "sm")}
          >
            Update
          </button>
        ) : (
          <div className="flex gap-2 text-sm">
            <button
              onClick={handleSave}
              disabled={saving}
              className={btn("primary", "sm")}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              disabled={saving}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="h-[250px] overflow-y-auto overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                {headers.map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isEditing ? (
                allClients.length === 0 ? (
                  <tr>
                    <td
                      colSpan={headers.length}
                      className="py-10 text-center text-gray-400 text-xs"
                    >
                      No clients available.
                    </td>
                  </tr>
                ) : (
                  allClients.map((c, index) => {
                    const isChecked = formState.client_ids.includes(c.id);
                    const rowBg = isChecked
                      ? "bg-brand-50"
                      : index % 2 === 0
                      ? "bg-gray-50"
                      : "bg-white";
                    return (
                      <tr
                        key={c.id}
                        onClick={() => handleMultiSelect("client_ids", c.id)}
                        className={`${rowBg} hover:bg-brand-50 transition-colors cursor-pointer`}
                      >
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleMultiSelect("client_ids", c.id)}
                            className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                          {clientName(c)}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                          {c.type || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                          <StatusBadge status={c.status} />
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                          {c.country || "—"}
                        </td>
                      </tr>
                    );
                  })
                )
              ) : pool.clients && pool.clients.length > 0 ? (
                pool.clients.map((c, index) => {
                  const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                  return (
                    <tr
                      key={c.id}
                      className={`${rowBg} hover:bg-brand-50 transition-colors`}
                    >
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                        {clientName(c)}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                        {c.type || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                        {c.country || "—"}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={headers.length}
                    className="py-10 text-center text-gray-400 text-xs"
                  >
                    No clients in this pool.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ClientsCard;
